import React, { FC, ReactNode, useContext, useState } from 'react';
import MapMarkerAlt from '~/components/Icons/outline/MapMarkerAlt';
import { EditAddressStyled, EditAddressQuoteStyled } from './EditAddressStyled';
import { PAGE_ROUTES } from '~/constants/pages';
import { FuelContext } from '~/providers/FuelProvider';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import { trackEvent } from '~/helpers/tracking';
import { resetAddressRoute } from '~/redux/actions/address';
import WarningModal from '../WarningModal';
import { setResume } from '~/redux/actions/common';
import { SaveQuoteButton } from '../Modal/screens/SaveQuote';
import { useMonarchSlot } from '@redventures/cohesion-utils-react';
import { isEmpty } from 'lodash';

interface Props {
  wrap?: boolean;
  title?: string;
  showTitle?: boolean;
  showAddress?: boolean;
  inline?: boolean;
  editable?: boolean;
  fontColorTheme?: 'dark' | 'light';
  editCopy?: ReactNode;
  includeCity?: boolean;
  includeState?: boolean;
  isAddressUpperCase?: boolean;
  showWarningModal?: boolean;
  isAiFlow?: boolean;
  showQuoteNumber?: boolean;
}

/**
 *
 * @param {string} [title] - Copy displayed next to the map icon.
 * @param {boolean} [showTitle=true] - Toggle to show the title.
 * @param {boolean} [showAddress=true] - Toggle to show the address.
 * @param {boolean} [editable] - Toggle to allow the address to be edited.
 * @param {'dark' | 'light'} [fontColorTheme='dark'] - Determines the color of the text.
 * @param {ReactNode} [editCopy='Edit'] - Copy displayed next to the address.
 * @param {boolean} [includeCity=false]   - Toggle to include the city in the address.
 * @param {boolean} [includeState=false] - Toggle to include the state in the address.
 * @param {boolean} [isAddressUpperCase=true] - Toggle to make the address uppercase.
 * @param {boolean} [showWarningModal=false] - Toggle to show a warning modal before editing.
 * @param {boolean} [isAiFlow=false] - Determines if this is currently in the AI flow, this will alter the page where the address is edited
 * @param {boolean} [showQuoteNumber=true] - Determines if current quote is displayed
 *
 * @returns {JSX.Element} The rendered EditAddress component.
 */

const EditAddress: FC<Props> = ({
  wrap,
  title,
  showTitle = true,
  showAddress = true,
  inline,
  editable,
  fontColorTheme = 'dark',
  editCopy = 'Edit',
  includeCity = false,
  includeState = false,
  isAddressUpperCase = true,
  showWarningModal = false,
  isAiFlow = false,
  showQuoteNumber = true,
}) => {
  const dispatch = useDispatch();
  const { router } = useContext(FuelContext);
  const { address: addy = {}, tracking } = useSelector((store: ReduxState) => store);
  const { address, zip } = addy?.install || {};
  const [showWarning, setShowWarning] = useState(false);
  const { data: saveQuoteSlot } = useMonarchSlot('saveQuote');
  const showSaveQuoteButton = !isEmpty(saveQuoteSlot);

  if (!address) return <EditAddressStyled inline={inline} />;

  const eventHandler = (event) => {
    event.preventDefault();

    if (event.key === 'Enter' || event.type === 'click') {
      if (showWarningModal) {
        setShowWarning(true);
      } else {
        proceedWithEdit();
      }
    }
  };

  const proceedWithEdit = () => {
    trackEvent({
      action: 'elementClicked',
      data: {
        text: 'edit address',
      },
    });
    trackEvent({ action: 'editAddress' });

    dispatch(resetAddressRoute());
    dispatch(setResume('acceptedResume', false));
    router.push(PAGE_ROUTES.ADDRESS_CHECK);
  };

  let addressText = `${address}, `;
  if (wrap) addressText += '\n';
  if (includeCity) addressText += `${addy?.install?.city}, `;
  if (includeState) addressText += `${addy?.install?.state} `;
  if (zip) addressText += `${zip}`;

  if (isAddressUpperCase) addressText = addressText.toUpperCase();

  return (
    <>
      <EditAddressStyled inline={inline} fontColorTheme={fontColorTheme}>
        {showQuoteNumber && tracking?.quoteNumber && !isAiFlow && (
          <EditAddressQuoteStyled showSaveQuoteButton={showSaveQuoteButton}>
            <span>Quote Number {tracking?.quoteNumber}</span>
            {showSaveQuoteButton && <SaveQuoteButton />}
          </EditAddressQuoteStyled>
        )}
        {showTitle && (
          <span className="edit-address__title">
            <MapMarkerAlt /> {title}
          </span>
        )}
        {editable ? (
          <button
            data-testid="edit-address__button"
            className="edit-address__button fs-mask"
            data-private="lipsum"
            tabIndex={0}
            onKeyDown={eventHandler}
            onClick={eventHandler}
          >
            {showAddress && addressText} <span>{editCopy}</span>
          </button>
        ) : (
          <span className="edit-address__address fs-mask" data-private="lipsum">
            {addressText}
          </span>
        )}
      </EditAddressStyled>

      <WarningModal
        visible={showWarning}
        onClose={() => setShowWarning(false)}
        onProceed={() => {
          setShowWarning(false);
          proceedWithEdit();
        }}
        headerText="Are you sure you want to edit your address?"
        subheaderText="You'll need to retake the quiz once you update your address."
        buttonText="Continue"
      />
    </>
  );
};

EditAddress.displayName = 'EditAddress';

EditAddress.defaultProps = {
  wrap: true,
  title: 'Service address:',
  editable: true,
};

export default EditAddress;
